<div class="login-page">

  <!-- ── Left panel: Brand + Features ─────────────────────────────────────── -->
  <aside class="lp-left">

    <!-- Brand row -->
    <div class="lp-brand">
      <div class="lp-brand-mark"></div>
      <span class="lp-brand-name">Book<em>Bridge</em></span>
    </div>

    <!-- Intro -->
    <div class="lp-intro">
      <div class="lp-eyebrow">
        <span class="lp-eyebrow-dot"></span>
        The library management platform
      </div>
      <h1 class="lp-h1">Run your whole library — <em>from members to deliveries</em> — in one place.</h1>
      <p class="lp-body">BookBridge is a modern, web-based library management system built for branches that serve members at the counter and at home.</p>
    </div>

    <!-- Setup pill -->
    <div class="lp-setup-pill">
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" width="15" height="15">
        <path d="M8.5 1.5L3 9h4l-.5 5.5L13 7H9l-.5-5.5z"/>
      </svg>
      <span>Set up in <strong>minutes</strong>, not weeks</span>
    </div>

    <!-- Rotating showcase: Features → Library → Delivery -->
    <div class="lp-showcase"
         [class.lp-showcase--paused]="showcasePaused"
         (mouseenter)="onScEnter()"
         (mouseleave)="onScLeave()">

      <!-- Slide 0: Feature list -->
      <div class="lp-slide lp-slide--features" [class.lp-slide--active]="showcaseScene === 0">
        <div class="lp-features" role="list">

          <div class="lp-feature" role="listitem">
            <div class="lp-glyph" aria-hidden="true">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="6" cy="6" r="2.4"/><path d="M2 13.4c.7-2 2.2-3 4-3s3.3 1 4 3"/>
                <circle cx="11.5" cy="5" r="1.8"/><path d="M10.6 9.5c.3-.1.6-.1.9-.1 1.6 0 2.7.9 3.2 2.6"/>
              </svg>
            </div>
            <h3>Easy member management</h3>
            <p>Add, renew, suspend, and merge member records in seconds.</p>
          </div>

          <div class="lp-feature" role="listitem">
            <div class="lp-glyph" aria-hidden="true">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
                <rect x="1.5" y="3" width="9" height="6.5" rx="1"/>
                <path d="M3.5 11.5h5"/>
                <rect x="11" y="6" width="3.5" height="7" rx="0.8"/>
                <path d="M12.25 11.5h1"/>
              </svg>
            </div>
            <h3>Web &amp; mobile, in sync</h3>
            <p>Native iOS &amp; Android apps, plus the full admin console on web.</p>
          </div>

          <div class="lp-feature" role="listitem">
            <div class="lp-glyph" aria-hidden="true">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
                <path d="M8 1.5l5.5 2v4.2c0 3-2.3 5.5-5.5 6.8-3.2-1.3-5.5-3.8-5.5-6.8V3.5L8 1.5z"/>
                <path d="M6 8l1.5 1.5L10.5 6.5"/>
              </svg>
            </div>
            <h3>4-level access control</h3>
            <p>Owner, Librarian, Counter Staff, and Runner — each role scoped.</p>
          </div>

          <div class="lp-feature" role="listitem">
            <div class="lp-glyph" aria-hidden="true">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
                <path d="M1.5 4.5h7v6h-7z"/>
                <path d="M8.5 6.5h3l2 2v2h-5z"/>
                <circle cx="5" cy="12" r="1.3"/>
                <circle cx="11.5" cy="12" r="1.3"/>
              </svg>
            </div>
            <h3>Counter or home delivery</h3>
            <p>Counter circulation and home-delivery routes from one catalogue.</p>
          </div>

          <div class="lp-feature" role="listitem">
            <div class="lp-glyph" aria-hidden="true">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="8" cy="8" r="6"/>
                <path d="M2 8h12"/>
                <path d="M8 2c1.8 2 2.7 4 2.7 6S9.8 14 8 14C6.2 14 5.3 12 5.3 8S6.2 2 8 2z"/>
              </svg>
            </div>
            <h3>Multilingual data support</h3>
            <p>Store and search records across scripts and languages.</p>
          </div>

          <div class="lp-feature" role="listitem">
            <div class="lp-glyph" aria-hidden="true">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="7" cy="7" r="4.2"/>
                <path d="M10.2 10.2L13.5 13.5"/>
              </svg>
            </div>
            <h3>Searches that just work</h3>
            <p>Fuzzy, multi-field search across catalogue, members, and loans.</p>
          </div>

          <div class="lp-feature" role="listitem">
            <div class="lp-glyph" aria-hidden="true">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
                <path d="M2 8.5l3.5 3.5L14 4"/>
                <path d="M2 12.5l1.5 1.5"/>
              </svg>
            </div>
            <h3>Quick onboarding</h3>
            <p>Spin up a branch in minutes with the guided CSV importer.</p>
          </div>

        </div>
      </div>

      <!-- Slide 1: Library counter scene -->
      <div class="lp-slide lp-slide--scene" [class.lp-slide--active]="showcaseScene === 1">
        <div class="lp-scene-card">
          <svg viewBox="0 0 540 340" preserveAspectRatio="xMidYMid slice" style="display:block;width:100%;height:100%">
            <rect width="540" height="340" fill="#f3e8c9"/>
            <rect width="540" height="120" fill="#faf2dd" opacity="0.7"/>
            <rect x="0" y="232" width="540" height="6" fill="#4d3522"/>
            <rect x="0" y="238" width="540" height="3" fill="#7a5535" opacity="0.5"/>
            <g><rect x="20" y="35" width="115" height="200" fill="#4d3522"/><rect x="26" y="41" width="103" height="188" fill="#4d3522"/><rect x="26" y="79" width="103" height="2.5" fill="#7a5535"/><rect x="26" y="119" width="103" height="2.5" fill="#7a5535"/><rect x="26" y="159" width="103" height="2.5" fill="#7a5535"/><rect x="26" y="199" width="103" height="2.5" fill="#7a5535"/><rect x="26" y="231" width="103" height="2.5" fill="#7a5535"/><rect x="30" y="51" width="10" height="28" fill="#caa168"/><rect x="41.5" y="49" width="10" height="30" fill="#f5ebcb"/><rect x="53" y="47" width="10" height="32" fill="#4a587a"/><rect x="64.5" y="45" width="10" height="34" fill="#9a553a"/><rect x="76" y="51" width="10" height="28" fill="#5a3b1f"/><rect x="87.5" y="49" width="10" height="30" fill="#caa168"/><rect x="99" y="47" width="10" height="32" fill="#f5ebcb"/><rect x="110.5" y="45" width="10" height="34" fill="#4a587a"/><rect x="30" y="87" width="10" height="32" fill="#c9b88a"/><rect x="41.5" y="85" width="10" height="34" fill="#3a4a6b"/><rect x="53" y="91" width="10" height="28" fill="#7a3a14"/><rect x="64.5" y="89" width="10" height="30" fill="#8a4030"/><rect x="76" y="87" width="10" height="32" fill="#6f8567"/><rect x="87.5" y="85" width="10" height="34" fill="#c9b88a"/><rect x="99" y="91" width="10" height="28" fill="#3a4a6b"/><rect x="110.5" y="89" width="10" height="30" fill="#7a3a14"/><rect x="30" y="131" width="10" height="28" fill="#caa168"/><rect x="41.5" y="129" width="10" height="30" fill="#f5ebcb"/><rect x="53" y="127" width="10" height="32" fill="#4a587a"/><rect x="64.5" y="125" width="10" height="34" fill="#9a553a"/><rect x="76" y="131" width="10" height="28" fill="#5a3b1f"/><rect x="87.5" y="129" width="10" height="30" fill="#caa168"/><rect x="99" y="127" width="10" height="32" fill="#f5ebcb"/><rect x="110.5" y="125" width="10" height="34" fill="#4a587a"/><rect x="30" y="167" width="10" height="32" fill="#c9b88a"/><rect x="41.5" y="165" width="10" height="34" fill="#3a4a6b"/><rect x="53" y="171" width="10" height="28" fill="#7a3a14"/><rect x="64.5" y="169" width="10" height="30" fill="#8a4030"/><rect x="76" y="167" width="10" height="32" fill="#6f8567"/><rect x="87.5" y="165" width="10" height="34" fill="#c9b88a"/><rect x="99" y="171" width="10" height="28" fill="#3a4a6b"/><rect x="110.5" y="169" width="10" height="30" fill="#7a3a14"/><rect x="77" y="41" width="2" height="188" fill="#7a5535" opacity="0.6"/><line x1="34" y1="45" x2="34" y2="221" stroke="#f5ebcb" stroke-width="0.5" opacity="0.08"/><rect x="18" y="31" width="119" height="4" fill="#7a5535"/><rect x="16" y="27" width="123" height="4" fill="#4d3522"/><circle cx="78" cy="217" r="1.6" fill="#caa168"/></g>
            <g><rect x="150" y="35" width="115" height="200" fill="#4d3522"/><rect x="156" y="41" width="103" height="188" fill="#4d3522"/><rect x="156" y="79" width="103" height="2.5" fill="#7a5535"/><rect x="156" y="119" width="103" height="2.5" fill="#7a5535"/><rect x="156" y="159" width="103" height="2.5" fill="#7a5535"/><rect x="156" y="199" width="103" height="2.5" fill="#7a5535"/><rect x="156" y="231" width="103" height="2.5" fill="#7a5535"/><rect x="160" y="51" width="10" height="28" fill="#6f8567"/><rect x="171.5" y="49" width="10" height="30" fill="#c9b88a"/><rect x="183" y="47" width="10" height="32" fill="#3a4a6b"/><rect x="194.5" y="45" width="10" height="34" fill="#7a3a14"/><rect x="206" y="51" width="10" height="28" fill="#8a4030"/><rect x="217.5" y="49" width="10" height="30" fill="#6f8567"/><rect x="229" y="47" width="10" height="32" fill="#c9b88a"/><rect x="240.5" y="45" width="10" height="34" fill="#3a4a6b"/><rect x="160" y="87" width="10" height="32" fill="#5a3b1f"/><rect x="171.5" y="85" width="10" height="34" fill="#caa168"/><rect x="183" y="91" width="10" height="28" fill="#f5ebcb"/><rect x="194.5" y="89" width="10" height="30" fill="#4a587a"/><rect x="206" y="87" width="10" height="32" fill="#9a553a"/><rect x="217.5" y="85" width="10" height="34" fill="#5a3b1f"/><rect x="229" y="91" width="10" height="28" fill="#caa168"/><rect x="240.5" y="89" width="10" height="30" fill="#f5ebcb"/><rect x="160" y="131" width="10" height="28" fill="#6f8567"/><rect x="171.5" y="129" width="10" height="30" fill="#c9b88a"/><rect x="183" y="127" width="10" height="32" fill="#3a4a6b"/><rect x="194.5" y="125" width="10" height="34" fill="#7a3a14"/><rect x="206" y="131" width="10" height="28" fill="#8a4030"/><rect x="217.5" y="129" width="10" height="30" fill="#6f8567"/><rect x="229" y="127" width="10" height="32" fill="#c9b88a"/><rect x="240.5" y="125" width="10" height="34" fill="#3a4a6b"/><rect x="160" y="167" width="10" height="32" fill="#5a3b1f"/><rect x="171.5" y="165" width="10" height="34" fill="#caa168"/><rect x="183" y="171" width="10" height="28" fill="#f5ebcb"/><rect x="194.5" y="169" width="10" height="30" fill="#4a587a"/><rect x="206" y="167" width="10" height="32" fill="#9a553a"/><rect x="217.5" y="165" width="10" height="34" fill="#5a3b1f"/><rect x="229" y="171" width="10" height="28" fill="#caa168"/><rect x="240.5" y="169" width="10" height="30" fill="#f5ebcb"/><rect x="207" y="41" width="2" height="188" fill="#7a5535" opacity="0.6"/><line x1="164" y1="45" x2="164" y2="221" stroke="#f5ebcb" stroke-width="0.5" opacity="0.08"/><rect x="148" y="31" width="119" height="4" fill="#7a5535"/><rect x="146" y="27" width="123" height="4" fill="#4d3522"/><circle cx="208" cy="217" r="1.6" fill="#caa168"/></g>
            <g><rect x="280" y="35" width="115" height="200" fill="#4d3522"/><rect x="286" y="41" width="103" height="188" fill="#4d3522"/><rect x="286" y="79" width="103" height="2.5" fill="#7a5535"/><rect x="286" y="119" width="103" height="2.5" fill="#7a5535"/><rect x="286" y="159" width="103" height="2.5" fill="#7a5535"/><rect x="286" y="199" width="103" height="2.5" fill="#7a5535"/><rect x="286" y="231" width="103" height="2.5" fill="#7a5535"/><rect x="290" y="51" width="10" height="28" fill="#9a553a"/><rect x="301.5" y="49" width="10" height="30" fill="#5a3b1f"/><rect x="313" y="47" width="10" height="32" fill="#caa168"/><rect x="324.5" y="45" width="10" height="34" fill="#f5ebcb"/><rect x="336" y="51" width="10" height="28" fill="#4a587a"/><rect x="347.5" y="49" width="10" height="30" fill="#9a553a"/><rect x="359" y="47" width="10" height="32" fill="#5a3b1f"/><rect x="370.5" y="45" width="10" height="34" fill="#caa168"/><rect x="290" y="87" width="10" height="32" fill="#8a4030"/><rect x="301.5" y="85" width="10" height="34" fill="#6f8567"/><rect x="313" y="91" width="10" height="28" fill="#c9b88a"/><rect x="324.5" y="89" width="10" height="30" fill="#3a4a6b"/><rect x="336" y="87" width="10" height="32" fill="#7a3a14"/><rect x="347.5" y="85" width="10" height="34" fill="#8a4030"/><rect x="359" y="91" width="10" height="28" fill="#6f8567"/><rect x="370.5" y="89" width="10" height="30" fill="#c9b88a"/><rect x="290" y="131" width="10" height="28" fill="#9a553a"/><rect x="301.5" y="129" width="10" height="30" fill="#5a3b1f"/><rect x="313" y="127" width="10" height="32" fill="#caa168"/><rect x="324.5" y="125" width="10" height="34" fill="#f5ebcb"/><rect x="336" y="131" width="10" height="28" fill="#4a587a"/><rect x="347.5" y="129" width="10" height="30" fill="#9a553a"/><rect x="359" y="127" width="10" height="32" fill="#5a3b1f"/><rect x="370.5" y="125" width="10" height="34" fill="#caa168"/><rect x="290" y="167" width="10" height="32" fill="#8a4030"/><rect x="301.5" y="165" width="10" height="34" fill="#6f8567"/><rect x="313" y="171" width="10" height="28" fill="#c9b88a"/><rect x="324.5" y="169" width="10" height="30" fill="#3a4a6b"/><rect x="336" y="167" width="10" height="32" fill="#7a3a14"/><rect x="347.5" y="165" width="10" height="34" fill="#8a4030"/><rect x="359" y="171" width="10" height="28" fill="#6f8567"/><rect x="370.5" y="169" width="10" height="30" fill="#c9b88a"/><rect x="337" y="41" width="2" height="188" fill="#7a5535" opacity="0.6"/><line x1="294" y1="45" x2="294" y2="221" stroke="#f5ebcb" stroke-width="0.5" opacity="0.08"/><rect x="278" y="31" width="119" height="4" fill="#7a5535"/><rect x="276" y="27" width="123" height="4" fill="#4d3522"/><circle cx="338" cy="217" r="1.6" fill="#caa168"/></g>
            <g><rect x="410" y="35" width="115" height="200" fill="#4d3522"/><rect x="416" y="41" width="103" height="188" fill="#4d3522"/><rect x="416" y="79" width="103" height="2.5" fill="#7a5535"/><rect x="416" y="119" width="103" height="2.5" fill="#7a5535"/><rect x="416" y="159" width="103" height="2.5" fill="#7a5535"/><rect x="416" y="199" width="103" height="2.5" fill="#7a5535"/><rect x="416" y="231" width="103" height="2.5" fill="#7a5535"/><rect x="420" y="51" width="10" height="28" fill="#7a3a14"/><rect x="431.5" y="49" width="10" height="30" fill="#8a4030"/><rect x="443" y="47" width="10" height="32" fill="#6f8567"/><rect x="454.5" y="45" width="10" height="34" fill="#c9b88a"/><rect x="466" y="51" width="10" height="28" fill="#3a4a6b"/><rect x="477.5" y="49" width="10" height="30" fill="#7a3a14"/><rect x="489" y="47" width="10" height="32" fill="#8a4030"/><rect x="500.5" y="45" width="10" height="34" fill="#6f8567"/><rect x="420" y="87" width="10" height="32" fill="#4a587a"/><rect x="431.5" y="85" width="10" height="34" fill="#9a553a"/><rect x="443" y="91" width="10" height="28" fill="#5a3b1f"/><rect x="454.5" y="89" width="10" height="30" fill="#caa168"/><rect x="466" y="87" width="10" height="32" fill="#f5ebcb"/><rect x="477.5" y="85" width="10" height="34" fill="#4a587a"/><rect x="489" y="91" width="10" height="28" fill="#9a553a"/><rect x="500.5" y="89" width="10" height="30" fill="#5a3b1f"/><rect x="420" y="131" width="10" height="28" fill="#7a3a14"/><rect x="431.5" y="129" width="10" height="30" fill="#8a4030"/><rect x="443" y="127" width="10" height="32" fill="#6f8567"/><rect x="454.5" y="125" width="10" height="34" fill="#c9b88a"/><rect x="466" y="131" width="10" height="28" fill="#3a4a6b"/><rect x="477.5" y="129" width="10" height="30" fill="#7a3a14"/><rect x="489" y="127" width="10" height="32" fill="#8a4030"/><rect x="500.5" y="125" width="10" height="34" fill="#6f8567"/><rect x="420" y="167" width="10" height="32" fill="#4a587a"/><rect x="431.5" y="165" width="10" height="34" fill="#9a553a"/><rect x="443" y="171" width="10" height="28" fill="#5a3b1f"/><rect x="454.5" y="169" width="10" height="30" fill="#caa168"/><rect x="466" y="167" width="10" height="32" fill="#f5ebcb"/><rect x="477.5" y="165" width="10" height="34" fill="#4a587a"/><rect x="489" y="171" width="10" height="28" fill="#9a553a"/><rect x="500.5" y="169" width="10" height="30" fill="#5a3b1f"/><rect x="467" y="41" width="2" height="188" fill="#7a5535" opacity="0.6"/><line x1="424" y1="45" x2="424" y2="221" stroke="#f5ebcb" stroke-width="0.5" opacity="0.08"/><rect x="408" y="31" width="119" height="4" fill="#7a5535"/><rect x="406" y="27" width="123" height="4" fill="#4d3522"/><circle cx="468" cy="217" r="1.6" fill="#caa168"/></g>
            <rect x="0" y="270" width="540" height="70" fill="#b08454"/>
            <rect x="0" y="270" width="540" height="2.5" fill="#6e4d2a" opacity="0.7"/>
            <g><rect x="40" y="250" width="460" height="50" fill="#7a5535"/><rect x="38" y="246" width="464" height="8" fill="#9c7148"/><rect x="38" y="246" width="464" height="2" fill="#f5ebcb" opacity="0.18"/><rect x="62" y="262" width="120" height="32" fill="none" stroke="#4d3522" stroke-width="1.2"/><rect x="210" y="262" width="120" height="32" fill="none" stroke="#4d3522" stroke-width="1.2"/><rect x="358" y="262" width="120" height="32" fill="none" stroke="#4d3522" stroke-width="1.2"/><rect x="40" y="298" width="460" height="3" fill="#000" opacity="0.4"/></g>
            <g><rect x="92" y="236" width="6" height="10" fill="#caa168"/><rect x="80" y="226" width="30" height="10" fill="#6f8567"/><ellipse cx="95" cy="240" rx="22" ry="3" fill="#caa168" opacity="0.15"/></g>
            <g><rect x="135" y="240" width="34" height="6" fill="#8a4030"/><rect x="138" y="234" width="34" height="6" fill="#caa168"/><rect x="133" y="228" width="34" height="6" fill="#f5ebcb"/><rect x="160" y="228" width="3" height="14" fill="#8a4030"/></g>
            <g><path d="M240,246 L322,246 L320,232 L281,234 L242,232 Z" fill="#fefae3"/><line x1="281" y1="234" x2="281" y2="246" stroke="#c9b88a" stroke-width="0.6"/><rect x="270" y="240" width="36" height="2.5" fill="#11162a" transform="rotate(-10 288 241)"/><rect x="304" y="240" width="6" height="2.5" fill="#caa168" transform="rotate(-10 307 241)"/></g>
            <g><rect x="400" y="226" width="50" height="20" fill="#c9b88a"/><rect x="400" y="226" width="50" height="6" fill="#f5ebcb"/><rect x="420" y="226" width="6" height="20" fill="#8a4030" opacity="0.7"/><line x1="400" y1="232" x2="450" y2="232" stroke="#4d3522" stroke-width="0.5"/></g>
            <g><rect x="245" y="180" width="40" height="68" fill="#6f8567" rx="2"/><path d="M255,180 L265,196 L275,180 Z" fill="#f5ebcb"/><circle cx="265" cy="168" r="11" fill="#f5ebcb"/><path d="M255,165 Q265,156 275,165 L275,172 Q265,166 255,172 Z" fill="#11162a" opacity="0.5"/><rect x="240" y="218" width="22" height="6" fill="#6f8567"/></g>
            <g><rect x="380" y="200" width="36" height="46" fill="#8a4030" rx="2"/><circle cx="398" cy="188" r="10" fill="#f5ebcb"/><path d="M389,185 Q398,178 407,185 L407,191 Q398,186 389,191 Z" fill="#11162a" opacity="0.5"/><rect x="412" y="218" width="22" height="6" fill="#8a4030"/></g>
            <g><line x1="130" y1="0" x2="130" y2="22" stroke="#a17b3e" stroke-width="1"/><path d="M118,22 L142,22 L138,36 L122,36 Z" fill="#caa168"/><ellipse cx="130" cy="48" rx="34" ry="14" fill="#caa168" opacity="0.05"/></g>
            <g><line x1="270" y1="0" x2="270" y2="22" stroke="#a17b3e" stroke-width="1"/><path d="M258,22 L282,22 L278,36 L262,36 Z" fill="#caa168"/><ellipse cx="270" cy="48" rx="34" ry="14" fill="#caa168" opacity="0.05"/></g>
            <g><line x1="410" y1="0" x2="410" y2="22" stroke="#a17b3e" stroke-width="1"/><path d="M398,22 L422,22 L418,36 L402,36 Z" fill="#caa168"/><ellipse cx="410" cy="48" rx="34" ry="14" fill="#caa168" opacity="0.05"/></g>
            <g opacity="0.9"><line x1="200" y1="0" x2="200" y2="14" stroke="#a17b3e" stroke-width="1"/><line x1="340" y1="0" x2="340" y2="14" stroke="#a17b3e" stroke-width="1"/><rect x="195" y="14" width="150" height="18" fill="#4d3522"/><rect x="197" y="16" width="146" height="14" fill="#7a5535"/><text x="270" y="27" fill="#f5ebcb" font-size="9" font-family="Crimson Pro,serif" font-style="italic" text-anchor="middle" letter-spacing="0.1em">CIRCULATION</text></g>
          </svg>
          <div class="lp-scene-caption">
            <span class="lp-cdot"></span>
            <b>In the library</b>
            <span class="lp-scene-txt">One platform runs the counter — circulation, members, and catalogue.</span>
          </div>
        </div>
      </div>

      <!-- Slide 2: Delivery van scene -->
      <div class="lp-slide lp-slide--scene" [class.lp-slide--active]="showcaseScene === 2">
        <div class="lp-scene-card">
          <svg viewBox="0 0 540 340" preserveAspectRatio="xMidYMid slice" style="display:block;width:100%;height:100%">
            <rect width="540" height="340" fill="#dbe5ec"/>
            <rect width="540" height="180" fill="#eaf0f4" opacity="0.6"/>
            <circle cx="80" cy="60" r="22" fill="#f5ebcb" opacity="0.16"/>
            <circle cx="80" cy="60" r="12" fill="#f5ebcb" opacity="0.22"/>
            <g opacity="0.55"><path d="M0,225 L70,225 L70,200 L110,200 L110,225 L170,225 L170,210 L240,210 L240,225 L0,225 Z" fill="#11162a"/><path d="M300,225 L380,225 L380,205 L440,205 L440,225 L540,225 L540,235 L300,235 Z" fill="#11162a"/><rect x="90" y="190" width="6" height="10" fill="#11162a"/><rect x="410" y="195" width="6" height="10" fill="#11162a"/></g>
            <g opacity="0.5"><rect x="265" y="195" width="3" height="32" fill="#11162a"/><circle cx="266" cy="195" r="14" fill="#11162a"/><circle cx="258" cy="200" r="11" fill="#11162a"/><circle cx="274" cy="200" r="11" fill="#11162a"/></g>
            <rect x="0" y="245" width="540" height="95" fill="#646a78"/>
            <rect x="0" y="245" width="540" height="2.5" fill="#000" opacity="0.5"/>
            <rect x="30" y="288" width="40" height="3" fill="#f5ebcb" opacity="0.35"/><rect x="110" y="288" width="40" height="3" fill="#f5ebcb" opacity="0.35"/><rect x="190" y="288" width="40" height="3" fill="#f5ebcb" opacity="0.35"/><rect x="270" y="288" width="40" height="3" fill="#f5ebcb" opacity="0.35"/><rect x="350" y="288" width="40" height="3" fill="#f5ebcb" opacity="0.35"/><rect x="430" y="288" width="40" height="3" fill="#f5ebcb" opacity="0.35"/><rect x="510" y="288" width="40" height="3" fill="#f5ebcb" opacity="0.35"/>
            <g><ellipse cx="245" cy="262" rx="170" ry="6" fill="#000" opacity="0.45"/><rect x="170" y="135" width="245" height="115" fill="#8a4030"/><rect x="170" y="135" width="245" height="8" fill="#5e2d22"/><rect x="170" y="242" width="245" height="8" fill="#5e2d22"/><path d="M90,170 L170,170 L170,250 L90,250 L80,225 L80,195 Z" fill="#8a4030"/><path d="M80,195 L90,170 L170,170 L170,195 Z" fill="#5e2d22" opacity="0.45"/><path d="M95,200 L140,200 L140,178 L98,178 Z" fill="#eaf0f4"/><path d="M95,200 L140,200 L140,178 L98,178 Z" fill="none" stroke="#11162a" stroke-width="1.4"/><rect x="145" y="178" width="22" height="22" fill="#eaf0f4" stroke="#11162a" stroke-width="1.4"/><circle cx="155" cy="194" r="6" fill="#11162a"/><rect x="146" y="198" width="18" height="6" fill="#11162a"/><ellipse cx="86" cy="218" rx="6" ry="5" fill="#caa168"/><ellipse cx="86" cy="218" rx="3" ry="2.5" fill="#f5ebcb"/><line x1="120" y1="200" x2="120" y2="248" stroke="#5e2d22" stroke-width="1.4"/><rect x="124" y="218" width="6" height="2" fill="#caa168"/><line x1="143" y1="200" x2="143" y2="248" stroke="#5e2d22" stroke-width="1.4" opacity="0.6"/><rect x="185" y="148" width="215" height="92" fill="#4d3522"/><rect x="185" y="168" width="215" height="3" fill="#7a5535"/><rect x="188" y="145" width="11" height="24" fill="#caa168"/><rect x="201" y="143" width="11" height="26" fill="#f5ebcb"/><rect x="214" y="141" width="11" height="28" fill="#4a587a"/><rect x="227" y="145" width="11" height="24" fill="#9a553a"/><rect x="240" y="143" width="11" height="26" fill="#5a3b1f"/><rect x="253" y="141" width="11" height="28" fill="#caa168"/><rect x="266" y="145" width="11" height="24" fill="#f5ebcb"/><rect x="279" y="143" width="11" height="26" fill="#4a587a"/><rect x="292" y="141" width="11" height="28" fill="#9a553a"/><rect x="305" y="145" width="11" height="24" fill="#5a3b1f"/><rect x="318" y="143" width="11" height="26" fill="#caa168"/><rect x="331" y="141" width="11" height="28" fill="#f5ebcb"/><rect x="344" y="145" width="11" height="24" fill="#4a587a"/><rect x="357" y="143" width="11" height="26" fill="#9a553a"/><rect x="370" y="141" width="11" height="28" fill="#5a3b1f"/><rect x="383" y="145" width="11" height="24" fill="#caa168"/><rect x="185" y="198" width="215" height="3" fill="#7a5535"/><rect x="188" y="173" width="11" height="26" fill="#6f8567"/><rect x="201" y="171" width="11" height="28" fill="#c9b88a"/><rect x="214" y="175" width="11" height="24" fill="#3a4a6b"/><rect x="227" y="173" width="11" height="26" fill="#7a3a14"/><rect x="240" y="171" width="11" height="28" fill="#8a4030"/><rect x="253" y="175" width="11" height="24" fill="#6f8567"/><rect x="266" y="173" width="11" height="26" fill="#c9b88a"/><rect x="279" y="171" width="11" height="28" fill="#3a4a6b"/><rect x="292" y="175" width="11" height="24" fill="#7a3a14"/><rect x="305" y="173" width="11" height="26" fill="#8a4030"/><rect x="318" y="171" width="11" height="28" fill="#6f8567"/><rect x="331" y="175" width="11" height="24" fill="#c9b88a"/><rect x="344" y="173" width="11" height="26" fill="#3a4a6b"/><rect x="357" y="171" width="11" height="28" fill="#7a3a14"/><rect x="370" y="175" width="11" height="24" fill="#8a4030"/><rect x="383" y="173" width="11" height="26" fill="#6f8567"/><rect x="185" y="228" width="215" height="3" fill="#7a5535"/><rect x="188" y="201" width="11" height="28" fill="#9a553a"/><rect x="201" y="205" width="11" height="24" fill="#5a3b1f"/><rect x="214" y="203" width="11" height="26" fill="#caa168"/><rect x="227" y="201" width="11" height="28" fill="#f5ebcb"/><rect x="240" y="205" width="11" height="24" fill="#4a587a"/><rect x="253" y="203" width="11" height="26" fill="#9a553a"/><rect x="266" y="201" width="11" height="28" fill="#5a3b1f"/><rect x="279" y="205" width="11" height="24" fill="#caa168"/><rect x="292" y="203" width="11" height="26" fill="#f5ebcb"/><rect x="305" y="201" width="11" height="28" fill="#4a587a"/><rect x="318" y="205" width="11" height="24" fill="#9a553a"/><rect x="331" y="203" width="11" height="26" fill="#5a3b1f"/><rect x="344" y="201" width="11" height="28" fill="#caa168"/><rect x="357" y="205" width="11" height="24" fill="#f5ebcb"/><rect x="370" y="203" width="11" height="26" fill="#4a587a"/><rect x="383" y="201" width="11" height="28" fill="#9a553a"/><rect x="320" y="216" width="60" height="22" fill="#7a5535"/><rect x="320" y="216" width="60" height="4" fill="#9c7148"/><line x1="350" y1="216" x2="350" y2="238" stroke="#4d3522" stroke-width="0.8"/><rect x="185" y="148" width="215" height="92" fill="none" stroke="#5e2d22" stroke-width="2"/><rect x="183" y="146" width="219" height="3" fill="#5e2d22"/><rect x="183" y="239" width="219" height="3" fill="#5e2d22"/><text x="292" y="143" fill="#f5ebcb" font-size="11" font-family="Crimson Pro,serif" font-weight="600" text-anchor="middle" letter-spacing="0.04em">BookBridge</text><g transform="translate(238,134)"><path d="M0,6 Q4,0 8,6" fill="none" stroke="#f5ebcb" stroke-width="1.2"/><line x1="-1" y1="7" x2="9" y2="7" stroke="#f5ebcb" stroke-width="0.8"/></g><circle cx="135" cy="252" r="18" fill="#11162a"/><circle cx="135" cy="252" r="11" fill="#dac8a4"/><circle cx="135" cy="252" r="3" fill="#11162a"/><circle cx="360" cy="252" r="18" fill="#11162a"/><circle cx="360" cy="252" r="11" fill="#dac8a4"/><circle cx="360" cy="252" r="3" fill="#11162a"/><path d="M117,252 A18,18 0 0 1 153,252" fill="none" stroke="#5e2d22" stroke-width="2"/><path d="M342,252 A18,18 0 0 1 378,252" fill="none" stroke="#5e2d22" stroke-width="2"/></g>
            <g><rect x="448" y="230" width="9" height="32" fill="#11162a"/><rect x="460" y="230" width="9" height="32" fill="#4a587a"/><path d="M442,232 L442,196 Q442,180 456,180 L466,180 Q480,180 480,196 L480,232 Z" fill="#6f8567"/><rect x="442" y="228" width="38" height="4" fill="#4f6248"/><circle cx="461" cy="170" r="10" fill="#f5ebcb"/><path d="M451,167 Q461,160 471,167 L471,173 Q461,168 451,173 Z" fill="#11162a" opacity="0.55"/><path d="M450,163 L472,163 L470,158 Q461,154 452,158 Z" fill="#4f6248"/><rect x="448" y="163" width="26" height="2" fill="#4f6248"/><rect x="436" y="208" width="50" height="18" fill="#6f8567"/><rect x="428" y="206" width="66" height="28" fill="#c9b88a"/><rect x="428" y="206" width="66" height="6" fill="#f5ebcb"/><rect x="455" y="206" width="6" height="28" fill="#8a4030" opacity="0.7"/><line x1="428" y1="212" x2="494" y2="212" stroke="#4d3522" stroke-width="0.6"/><rect x="478" y="232" width="14" height="9" fill="#fefae3"/><rect x="478" y="232" width="14" height="2" fill="#8a4030"/></g>
          </svg>
          <div class="lp-scene-caption">
            <span class="lp-cdot"></span>
            <b>On the go</b>
            <span class="lp-scene-txt">The same platform powers home delivery, swaps, and field returns.</span>
          </div>
        </div>
      </div>

    </div>

    <!-- Showcase dot navigation -->
    <div class="lp-showcase-dots">
      <button type="button" class="lp-sc-dot" [class.lp-sc-dot--on]="showcaseScene === 0" (click)="setScene(0)">
        <span class="lp-pip"></span>Features
      </button>
      <button type="button" class="lp-sc-dot" [class.lp-sc-dot--on]="showcaseScene === 1" (click)="setScene(1)">
        <span class="lp-pip"></span>Library
      </button>
      <button type="button" class="lp-sc-dot" [class.lp-sc-dot--on]="showcaseScene === 2" (click)="setScene(2)">
        <span class="lp-pip"></span>Delivery
      </button>
      <span class="lp-sc-progress">
        <i *ngIf="progressVisible"></i>
      </span>
    </div>

    <!-- Footer -->
    <div class="lp-foot">
      <span>© {{ currentYear }} bookbridge.co.in</span>
      <span class="lp-powered">Powered by <a href="https://www.viotalworks.com" target="_blank" rel="noopener">Viotal Works Pvt Ltd</a></span>
    </div>

  </aside>

  <!-- ── Right panel: Login form ──────────────────────────────────────────── -->
  <main class="lp-right">
    <div class="lp-form-wrap">

      <div class="lp-card-eyebrow">
        <span class="lp-card-pin"></span> Get on the bridge to books
      </div>
      <h2 class="lp-card-title">Welcome back to BookBridge.</h2>
      <p class="lp-card-sub">Sign in with your library access code to manage deliveries, returns, and members engagement.</p>

      <div class="lp-error-box" *ngIf="errorMessage">
        <i class="pe-7s-close-circle"></i>
        <span>{{ errorMessage }}</span>
      </div>

      <div class="lp-license-warn" *ngIf="licenseWarning">
        <i class="pe-7s-attention"></i>
        <div>
          <div>{{ licenseWarning }}</div>
          <div class="lp-license-redirect">Redirecting in a moment…</div>
        </div>
      </div>

      <form [formGroup]="loginForm" (ngSubmit)="onSubmit()" novalidate>

        <!-- Username / Email -->
        <div class="lp-field">
          <label class="lp-label" for="lp-email">Username</label>
          <div class="lp-input-wrap">
            <span class="lp-lead">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round">
                <circle cx="8" cy="6" r="2.5"/><path d="M3 13.5c1-2.2 2.8-3.5 5-3.5s4 1.3 5 3.5"/>
              </svg>
            </span>
            <input #emailInput id="lp-email" type="email" formControlName="email"
              [class.lp-invalid]="email.invalid && email.touched"
              placeholder="e.g. admin@bookbridge.co.in" autocomplete="email"
              (keydown.enter)="passwordInput.focus()" />
          </div>
          <span class="lp-err" *ngIf="email.invalid && email.touched">
            <ng-container *ngIf="email.errors?.['required']">Email is required.</ng-container>
            <ng-container *ngIf="email.errors?.['email']">Enter a valid email address.</ng-container>
          </span>
        </div>

        <!-- Password -->
        <div class="lp-field">
          <div class="lp-label-row">
            <label class="lp-label" for="lp-pw">Password</label>
            <button type="button" class="lp-forgot-btn">Forgot password?</button>
          </div>
          <div class="lp-input-wrap">
            <span class="lp-lead">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round">
                <rect x="3" y="7" width="10" height="7" rx="1.5"/><path d="M5.5 7V5a2.5 2.5 0 015 0v2"/>
              </svg>
            </span>
            <input #passwordInput id="lp-pw" [type]="showPassword ? 'text' : 'password'" formControlName="password"
              [class.lp-invalid]="password.invalid && password.touched"
              placeholder="Minimum 6 characters" autocomplete="current-password" class="lp-pw-input"
              (keydown.enter)="clientCodeInput.focus()" />
            <button type="button" class="lp-eye-btn" (click)="togglePassword()" [attr.aria-label]="showPassword ? 'Hide password' : 'Show password'">
              <svg *ngIf="!showPassword" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round">
                <path d="M2 8s2.2-4 6-4 6 4 6 4-2.2 4-6 4-6-4-6-4z"/><circle cx="8" cy="8" r="2"/>
              </svg>
              <svg *ngIf="showPassword" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round">
                <path d="M2 8s2.2-4 6-4c1.1 0 2 .25 2.85.65M14 8s-2.2 4-6 4c-1.1 0-2-.25-2.85-.65"/>
                <path d="M5.4 5.4a3 3 0 014.2 4.2"/><path d="M2 2l12 12"/>
              </svg>
            </button>
          </div>
          <span class="lp-err" *ngIf="password.invalid && password.touched">
            <ng-container *ngIf="password.errors?.['required']">Password is required.</ng-container>
            <ng-container *ngIf="password.errors?.['minlength']">Must be at least 6 characters.</ng-container>
          </span>
        </div>

        <!-- Access Code -->
        <div class="lp-field">
          <label class="lp-label" for="lp-code">Access code</label>
          <div class="lp-input-wrap">
            <span class="lp-lead">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round">
                <rect x="2.5" y="6.5" width="11" height="7" rx="1.5"/><path d="M5 6.5V4.5a3 3 0 016 0v2"/>
              </svg>
            </span>
            <input #clientCodeInput id="lp-code" type="text" formControlName="clientCode"
              [class.lp-invalid]="clientCode.invalid && clientCode.touched"
              placeholder="Enter access code" autocomplete="organization" class="lp-access-input"
              (keydown.enter)="signInBtn.click()" />
          </div>
          <span class="lp-err" *ngIf="clientCode.invalid && clientCode.touched">
            <ng-container *ngIf="clientCode.errors?.['required']">Access code is required.</ng-container>
          </span>
        </div>

        <!-- Remember + Forgot -->
        <div class="lp-row-aux">
          <label class="lp-check">
            <input type="checkbox" checked />
            <span class="lp-check-box"></span>
            <span>Keep me signed in</span>
          </label>
        </div>

        <!-- Submit -->
        <button #signInBtn type="submit" class="lp-btn-signin" [disabled]="isLoading || !!licenseWarning">
          <span *ngIf="!isLoading">
            Sign in
            <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round">
              <path d="M2 7h10M8 3l4 4-4 4"/>
            </svg>
          </span>
          <span *ngIf="isLoading" class="lp-loader-row">
            <span class="spin-ring"></span> Signing in&hellip;
          </span>
        </button>

      </form>

      <div class="lp-card-foot">
        <button type="button" class="lp-onboard-btn" (click)="openOnboard()">
          <i class="pe-7s-rocket"></i> OnBoard
        </button>
        <span class="lp-copyright">© {{ currentYear }} bookbridge.co.in</span>
      </div>

    </div>
  </main>

</div>

<!-- ══ Onboard Modal ══════════════════════════════════════════════════════════ -->
<div class="ob-overlay" *ngIf="showOnboardModal" (click)="closeOnboard()"></div>

<div class="ob-modal" [class.ob-modal--wide]="onboardView === 'subscribers'" *ngIf="showOnboardModal">

  <!-- Header -->
  <div class="ob-header">
    <span class="ob-title"><i class="pe-7s-rocket"></i> OnBoard Admin</span>
    <button class="ob-close" type="button" (click)="closeOnboard()">
      <i class="pe-7s-close"></i>
    </button>
  </div>

  <!-- Error banner -->
  <div class="ob-error" *ngIf="onboardError">
    <i class="pe-7s-close-circle"></i> {{ onboardError }}
  </div>

  <!-- Loading -->
  <div class="ob-loader" *ngIf="onboardLoading">
    <span class="spin-ring"></span> Please wait&hellip;
  </div>

  <!-- ── View: Creator Login ── -->
  <ng-container *ngIf="onboardView === 'login' && !onboardLoading">
    <form [formGroup]="creatorLoginForm" (ngSubmit)="onCreatorLogin()" novalidate>
      <p class="ob-sub">Sign in to manage subscribers.</p>

      <div class="ob-field">
        <label class="ob-label">Username</label>
        <div class="ob-iw">
          <input type="text" formControlName="name" placeholder="Enter username"
            [class.invalid]="clf['name'].invalid && clf['name'].touched" />
          <i class="pe-7s-user ob-icon"></i>
        </div>
        <span class="ob-err" *ngIf="clf['name'].invalid && clf['name'].touched">Username is required.</span>
      </div>

      <div class="ob-field">
        <label class="ob-label">Password</label>
        <div class="ob-iw">
          <input [type]="showCreatorPassword ? 'text' : 'password'" formControlName="password"
            placeholder="Enter password" [class.invalid]="clf['password'].invalid && clf['password'].touched" />
          <i class="pe-7s-lock ob-icon"></i>
          <button type="button" class="ob-eye" (click)="showCreatorPassword = !showCreatorPassword">
            <i [class]="showCreatorPassword ? 'pe-7s-close-circle' : 'pe-7s-look'"></i>
          </button>
        </div>
        <span class="ob-err" *ngIf="clf['password'].invalid && clf['password'].touched">Password is required.</span>
      </div>

      <button type="submit" class="ob-btn-primary">
        <i class="pe-7s-power"></i> Sign In
      </button>
    </form>
  </ng-container>

  <!-- ── View: Dashboard ── -->
  <ng-container *ngIf="onboardView === 'dashboard' && !onboardLoading && stats">

    <div class="ob-stats-grid">
      <div class="ob-stat-card ob-stat-total">
        <div class="ob-stat-value">{{ stats.total }}</div>
        <div class="ob-stat-label">Total Subscribers</div>
      </div>
      <div class="ob-stat-card ob-stat-suspended">
        <div class="ob-stat-value">{{ stats.suspended }}</div>
        <div class="ob-stat-label">Suspended</div>
      </div>
      <div class="ob-stat-card ob-stat-renewal">
        <div class="ob-stat-value">{{ stats.renewalsDue }}</div>
        <div class="ob-stat-label">
          Renewals due in&nbsp;
          <input type="number" class="ob-days-input" [value]="renewalDays" (change)="onRenewalDaysChange($event)"
            min="1" max="365" />&nbsp;days
        </div>
      </div>
    </div>

    <div class="ob-dash-btns">
      <button type="button" class="ob-btn-primary" (click)="showSubscribersList()">
        <i class="pe-7s-users"></i> View Subscribers
      </button>
      <button type="button" class="ob-btn-secondary" (click)="showAddSubscriber()">
        <i class="pe-7s-plus"></i> Add Subscriber
      </button>
    </div>
    <button type="button" class="ob-change-pwd-link" (click)="showChangePwd()">
      <i class="pe-7s-config"></i> Change Password
    </button>

  </ng-container>

  <!-- ── View: Add Subscriber ── -->
  <ng-container *ngIf="onboardView === 'add-subscriber' && !onboardLoading">
    <form [formGroup]="subscriberForm" (ngSubmit)="onSaveSubscriber()" novalidate>

      <h3 class="ob-form-title">New Subscriber</h3>

      <div class="ob-section-title">Organization Details</div>

      <div class="ob-field">
        <label class="ob-label">Organization Name <span class="ob-req">*</span></label>
        <input type="text" formControlName="clientName" placeholder="e.g. City Public Library"
          [class.invalid]="sf['clientName'].invalid && sf['clientName'].touched" />
        <span class="ob-err" *ngIf="sf['clientName'].invalid && sf['clientName'].touched">Required.</span>
      </div>

      <div class="ob-row-2">
        <div class="ob-field">
          <label class="ob-label">Contact Person <span class="ob-req">*</span></label>
          <input type="text" formControlName="contactPerson" placeholder="Full name"
            [class.invalid]="sf['contactPerson'].invalid && sf['contactPerson'].touched" />
          <span class="ob-err" *ngIf="sf['contactPerson'].invalid && sf['contactPerson'].touched">Required.</span>
        </div>
        <div class="ob-field">
          <label class="ob-label">Phone <span class="ob-req">*</span></label>
          <input type="tel" formControlName="phone" placeholder="Mobile number"
            [class.invalid]="sf['phone'].invalid && sf['phone'].touched" />
          <span class="ob-err" *ngIf="sf['phone'].invalid && sf['phone'].touched">Required.</span>
        </div>
      </div>

      <div class="ob-row-2">
        <div class="ob-field">
          <label class="ob-label">Email <span class="ob-req">*</span></label>
          <input type="email" formControlName="email" placeholder="contact@org.com"
            [class.invalid]="sf['email'].invalid && sf['email'].touched" />
          <span class="ob-err" *ngIf="sf['email'].invalid && sf['email'].touched">Valid email required.</span>
        </div>
        <div class="ob-field">
          <label class="ob-label">Subscription Valid Till <span class="ob-req">*</span></label>
          <input type="date" formControlName="subscriptionValidTill"
            [class.invalid]="sf['subscriptionValidTill'].invalid && sf['subscriptionValidTill'].touched" />
          <span class="ob-err"
            *ngIf="sf['subscriptionValidTill'].invalid && sf['subscriptionValidTill'].touched">Required.</span>
        </div>
      </div>

      <div class="ob-field">
        <label class="ob-label">Address</label>
        <input type="text" formControlName="address" placeholder="Full address (optional)" />
      </div>

      <div class="ob-field">
        <label class="ob-label">Login Password <span class="ob-label-hint">(contact person's app password)</span> <span
            class="ob-req">*</span></label>
        <div class="ob-pw-wrap">
          <input [type]="showAdminPassword ? 'text' : 'password'" formControlName="adminPassword" class="ob-pw-input"
            placeholder="Min 6 characters"
            [class.invalid]="sf['adminPassword'].invalid && sf['adminPassword'].touched" />
          <button type="button" class="ob-pw-eye" (click)="showAdminPassword = !showAdminPassword">
            <i [class]="showAdminPassword ? 'pe-7s-close-circle' : 'pe-7s-look'"></i>
          </button>
        </div>
        <span class="ob-err" *ngIf="sf['adminPassword'].invalid && sf['adminPassword'].touched">
          <ng-container *ngIf="sf['adminPassword'].errors?.['required']">Password is required.</ng-container>
          <ng-container *ngIf="sf['adminPassword'].errors?.['minlength']">Minimum 6 characters.</ng-container>
        </span>
      </div>

      <div class="ob-form-actions">
        <button type="button" class="ob-btn-secondary" (click)="backToDashboard()">Cancel</button>
        <button type="submit" class="ob-btn-primary">
          <i class="pe-7s-diskette"></i> Save Subscriber
        </button>
      </div>

    </form>
  </ng-container>

  <!-- ── View: Success ── -->
  <ng-container *ngIf="onboardView === 'success' && !onboardLoading">
    <div class="ob-success">
      <div class="ob-success-icon"><i class="pe-7s-check"></i></div>
      <h3>Subscriber Added!</h3>
      <p>{{ newSubscriberName }} has been registered successfully.</p>
      <div class="ob-code-box">
        <div class="ob-code-label">Access Code</div>
        <div class="ob-code-value">{{ newAccessCode }}</div>
        <div class="ob-code-note">Share this code with the subscriber. It is required to log in.</div>
      </div>
      <button type="button" class="ob-btn-primary" (click)="backToDashboard()">
        <i class="pe-7s-back"></i> Back to Dashboard
      </button>
    </div>
  </ng-container>

  <!-- ── View: Subscriber List ── -->
  <ng-container *ngIf="onboardView === 'subscribers' && !onboardLoading">

    <!-- Renew overlay -->
    <div class="ob-renew-overlay" *ngIf="showRenewModal">
      <div class="ob-renew-card">
        <div class="ob-renew-title">Renew Subscription</div>
        <div class="ob-renew-org">{{ renewTarget?.clientName }}</div>
        <div class="ob-field">
          <label class="ob-label">New Valid Till <span class="ob-req">*</span></label>
          <input type="date" [(ngModel)]="renewDate" />
        </div>
        <div class="ob-err ob-renew-err" *ngIf="renewError">{{ renewError }}</div>
        <div class="ob-success-msg" *ngIf="renewSuccess"><i class="pe-7s-check"></i> {{ renewSuccess }}</div>
        <div class="ob-form-actions">
          <button type="button" class="ob-btn-secondary" (click)="closeRenewModal()" [disabled]="renewLoading">Cancel</button>
          <button type="button" class="ob-btn-primary" (click)="confirmRenew()" [disabled]="renewLoading || !renewDate">
            <span *ngIf="!renewLoading"><i class="pe-7s-check"></i> Renew</span>
            <span *ngIf="renewLoading" class="lp-loader-row"><span class="spin-ring"></span></span>
          </button>
        </div>
      </div>
    </div>

    <!-- Search + add -->
    <div class="ob-sub-toolbar">
      <input type="text" class="ob-search-input" [(ngModel)]="subscriberSearch"
        placeholder="Search by name, contact, phone, code…" />
      <button type="button" class="ob-btn-secondary ob-add-small" (click)="showAddSubscriber()">
        <i class="pe-7s-plus"></i> Add
      </button>
    </div>

    <div class="ob-subs-loading" *ngIf="subscribersLoading">
      <span class="spin-ring"></span> Loading subscribers…
    </div>

    <div class="ob-table-wrap" *ngIf="!subscribersLoading">
      <table class="ob-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Org Name</th>
            <th>Contact</th>
            <th>Valid Till</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let sub of filteredSubscribers; let i = index">
            <td class="ob-td-num">{{ i + 1 }}</td>
            <td class="ob-td-main">
              <div class="ob-sub-name">{{ sub.clientName }}</div>
              <div class="ob-sub-code">{{ sub.clientCode }}</div>
            </td>
            <td>
              <div>{{ sub.contactPerson }}</div>
              <div class="ob-sub-phone">{{ sub.phone }}</div>
            </td>
            <td>{{ sub.subscriptionValidTill | appDate }}</td>
            <td>
              <span class="ob-badge"
                [class.ob-badge--active]="sub.isActive !== false"
                [class.ob-badge--suspended]="sub.isActive === false">
                {{ sub.isActive !== false ? 'Active' : 'Suspended' }}
              </span>
            </td>
            <td class="ob-td-actions">
              <button type="button" class="ob-action-btn"
                [class.ob-action-suspend]="sub.isActive !== false"
                [class.ob-action-activate]="sub.isActive === false"
                [disabled]="actionLoadingMap[sub.clientCode]"
                (click)="toggleStatus(sub)">
                {{ sub.isActive !== false ? 'Suspend' : 'Activate' }}
              </button>
              <button type="button" class="ob-action-btn ob-action-renew"
                [disabled]="actionLoadingMap[sub.clientCode]"
                (click)="openRenewModal(sub)">
                Renew
              </button>
            </td>
          </tr>
          <tr *ngIf="filteredSubscribers.length === 0">
            <td colspan="6" class="ob-td-empty">No subscribers found.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="ob-back-bar">
      <button type="button" class="ob-btn-secondary" (click)="backToDashboard()">
        <i class="pe-7s-back"></i> Dashboard
      </button>
    </div>

  </ng-container>

  <!-- ── View: Change Password ── -->
  <ng-container *ngIf="onboardView === 'change-password' && !onboardLoading">
    <h3 class="ob-form-title">Change Password</h3>

    <form [formGroup]="changePwdForm" (ngSubmit)="onChangePwd()" novalidate>

      <div class="ob-field">
        <label class="ob-label">Current Password <span class="ob-req">*</span></label>
        <div class="ob-pw-wrap">
          <input [type]="showCurrentPwd ? 'text' : 'password'" formControlName="currentPassword"
            class="ob-pw-input" placeholder="Enter current password"
            [class.invalid]="changePwdForm.get('currentPassword')!.invalid && changePwdForm.get('currentPassword')!.touched" />
          <button type="button" class="ob-pw-eye" (click)="showCurrentPwd = !showCurrentPwd">
            <i [class]="showCurrentPwd ? 'pe-7s-close-circle' : 'pe-7s-look'"></i>
          </button>
        </div>
        <span class="ob-err" *ngIf="changePwdForm.get('currentPassword')!.invalid && changePwdForm.get('currentPassword')!.touched">Required.</span>
      </div>

      <div class="ob-field">
        <label class="ob-label">New Password <span class="ob-req">*</span></label>
        <div class="ob-pw-wrap">
          <input [type]="showNewPwd ? 'text' : 'password'" formControlName="newPassword"
            class="ob-pw-input" placeholder="Minimum 6 characters"
            [class.invalid]="changePwdForm.get('newPassword')!.invalid && changePwdForm.get('newPassword')!.touched" />
          <button type="button" class="ob-pw-eye" (click)="showNewPwd = !showNewPwd">
            <i [class]="showNewPwd ? 'pe-7s-close-circle' : 'pe-7s-look'"></i>
          </button>
        </div>
        <span class="ob-err" *ngIf="changePwdForm.get('newPassword')!.invalid && changePwdForm.get('newPassword')!.touched">
          <ng-container *ngIf="changePwdForm.get('newPassword')!.errors?.['required']">Required.</ng-container>
          <ng-container *ngIf="changePwdForm.get('newPassword')!.errors?.['minlength']">Minimum 6 characters.</ng-container>
        </span>
      </div>

      <div class="ob-field">
        <label class="ob-label">Confirm New Password <span class="ob-req">*</span></label>
        <div class="ob-pw-wrap">
          <input type="password" formControlName="confirmPassword"
            class="ob-pw-input" placeholder="Repeat new password"
            [class.invalid]="changePwdForm.get('confirmPassword')!.invalid && changePwdForm.get('confirmPassword')!.touched" />
        </div>
        <span class="ob-err" *ngIf="changePwdForm.get('confirmPassword')!.invalid && changePwdForm.get('confirmPassword')!.touched">Required.</span>
      </div>

      <div class="ob-err ob-change-err" *ngIf="changePwdError">{{ changePwdError }}</div>
      <div class="ob-success-msg" *ngIf="changePwdSuccess"><i class="pe-7s-check"></i> {{ changePwdSuccess }}</div>

      <div class="ob-form-actions">
        <button type="button" class="ob-btn-secondary" (click)="backToDashboard()" [disabled]="changePwdLoading">Cancel</button>
        <button type="submit" class="ob-btn-primary" [disabled]="changePwdLoading">
          <span *ngIf="!changePwdLoading"><i class="pe-7s-diskette"></i> Update Password</span>
          <span *ngIf="changePwdLoading" class="lp-loader-row"><span class="spin-ring"></span></span>
        </button>
      </div>

    </form>
  </ng-container>

</div>
